/**
 * HeaderAvatarButton
 *
 * The entry point to Settings on mobile. Settings used to be the fifth bottom
 * tab, which left every tab ~68px on a 344px phone — too narrow for the French
 * labels ("Garde-manger", "Paramètres"), so they rendered clipped (#531).
 * Dropping to four tabs buys each one ~86px; Settings moves up here instead.
 *
 * For a guest it also carries « Se connecter » (#741), on every tab but Mes
 * Repas. The only sign-in button
 * used to sit inside Settings, behind this unlabelled icon, so a cook with an
 * account who opened the app on a new device could not find a way in.
 *
 * Hidden on desktop web, where DesktopSidebar already carries a Settings entry.
 */

import React from 'react';
import { Pressable, Text, Image, View, StyleSheet, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../styles/theme';
import { RootState } from '../redux/store';
import { isDesktopWeb } from '../utils/platformDetection';
import { trackEvent } from '../services/analyticsService';
import { initialFor, guestSignInVariant } from './headerAvatarInitial';

const SIZE = 32;

interface Props {
  /** A header with no room for the label: the guest's sign-in stays an icon. */
  compact?: boolean;
  /**
   * Mes Repas passes false: its header is already full, and the decision on
   * #741 was to keep the week's own actions there and nothing else.
   */
  signIn?: boolean;
}

export const HeaderAvatarButton: React.FC<Props> = ({ compact = false, signIn = true }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { user, isGuest, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Desktop already reaches Settings through the sidebar — no duplicate entry.
  if (isDesktopWeb()) return null;

  const signedOut = isGuest || !isAuthenticated || !user;
  const initial = signedOut ? null : initialFor(user?.displayName, user?.email);

  const avatar = (
    <Pressable
      onPress={() => router.push('/settings')}
      hitSlop={8}
      accessibilityRole="button"
      accessibilityLabel={t('navigation.settings')}
      testID="header-avatar-button"
      style={styles.button}
    >
      {user?.photoURL ? (
        <Image source={{ uri: user.photoURL }} style={styles.photo} />
      ) : initial ? (
        <Text style={styles.initial}>{initial}</Text>
      ) : (
        <Feather name={signedOut && signIn ? 'settings' : 'user'} size={17} color={Paprika.accent} />
      )}
    </Pressable>
  );

  if (!signedOut || !signIn) return avatar;

  const variant = guestSignInVariant(width, compact);

  return (
    <View style={styles.guestRow}>
      <Pressable
        onPress={() => {
          trackEvent('guest_sign_in_clicked', { source: 'header', variant });
          router.push('/auth');
        }}
        hitSlop={6}
        accessibilityRole="button"
        accessibilityLabel={t('auth.signIn')}
        testID="header-sign-in"
        style={variant === 'label' ? styles.signIn : styles.signInIcon}
      >
        <Feather name="log-in" size={15} color={Paprika.onPrimary} />
        {variant === 'label' && (
          <Text style={styles.signInText} numberOfLines={1}>{t('auth.signIn')}</Text>
        )}
      </Pressable>
      {avatar}
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    width: SIZE,
    height: SIZE,
    borderRadius: SIZE / 2,
    backgroundColor: Paprika.accentLight,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  photo: {
    width: SIZE,
    height: SIZE,
    borderRadius: SIZE / 2,
  },
  initial: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    lineHeight: 16,
    color: Paprika.accent,
  },
  guestRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  signIn: {
    height: SIZE,
    borderRadius: SIZE / 2,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Paprika.accent,
  },
  signInIcon: {
    width: SIZE,
    height: SIZE,
    borderRadius: SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.accent,
  },
  signInText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    lineHeight: 16,
    color: Paprika.onPrimary,
  },
});

export default HeaderAvatarButton;
